class ImageCacheService {
  constructor() {
    this.dbName = "BibeImageCache";
    this.dbVersion = 1;
    this.storeName = "profileImages";
    this.db = null;
    this.cacheExpiration = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
    this.maxCacheSize = 50; // Maximum number of images to cache

    // Track in-flight requests to prevent duplicate fetches
    this.inFlightRequests = new Map();

    // Retry strategies similar to Groq service
    this.retryStrategies = [
      {
        name: "cache_lookup",
        description: "Check IndexedDB cache first",
        maxDelay: 0,
      },
      {
        name: "direct_fetch",
        description: "Fetch from Google servers",
        maxDelay: 1000,
      },
      {
        name: "retry_with_delay",
        description: "Retry after rate limit delay",
        maxDelay: 3000,
      },
      {
        name: "fallback_avatar",
        description: "Use default avatar fallback",
        maxDelay: 0,
      },
    ];

    this.init();
  }

  /**
   * Initialize IndexedDB connection
   */
  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        console.error("ImageCacheService: Failed to open IndexedDB");
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log("✅ ImageCacheService: IndexedDB initialized");
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, {
            keyPath: "id"
          });

          // Create indexes for efficient querying
          store.createIndex("url", "originalUrl", { unique: false });
          store.createIndex("timestamp", "timestamp", { unique: false });
          store.createIndex("userId", "userId", { unique: false });

          console.log("✅ ImageCacheService: Object store created");
        }
      };
    });
  }

  /**
   * Generate cache key from URL or user ID
   */
  generateCacheKey(url, userId = null) {
    if (userId) {
      return `user_${userId}`;
    }

    // Create hash from URL for consistent key
    let hash = 0;
    for (let i = 0; i < url.length; i++) {
      const char = url.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return `url_${Math.abs(hash)}`;
  }

  /**
   * Get cached image with retry strategies and request deduplication
   * @param {string} url - Image URL to fetch
   * @param {string} userId - User ID for cache key
   * @param {string} displayName - User's display name for fallback avatar
   */
  async getCachedImage(url, userId = null, displayName = null) {
    const requestKey = this.generateCacheKey(url, userId);

    // Check if there's already a request in flight for this image
    if (this.inFlightRequests.has(requestKey)) {
      console.log(`🔄 Deduplicating request for: ${url.substring(0, 50)}...`);
      return this.inFlightRequests.get(requestKey);
    }

    // Create new request promise
    const requestPromise = this._performImageFetch(url, userId, displayName);

    // Track the in-flight request
    this.inFlightRequests.set(requestKey, requestPromise);

    // Clean up tracking when request completes (success or failure)
    requestPromise.finally(() => {
      this.inFlightRequests.delete(requestKey);
    });

    return requestPromise;
  }

  /**
   * Internal method to perform the actual image fetch with retry strategies
   * @param {string} url - Image URL to fetch
   * @param {string} userId - User ID for cache key
   * @param {string} displayName - User's display name for fallback avatar
   */
  async _performImageFetch(url, userId, displayName) {
    let lastError = null;
    let isRateLimited = false;

    for (let attempt = 0; attempt < this.retryStrategies.length; attempt++) {
      const strategy = this.retryStrategies[attempt];

      try {
        console.log(`🔄 Image Strategy ${attempt + 1}/${this.retryStrategies.length}: ${strategy.description}`);

        switch (strategy.name) {
          case "cache_lookup":
            const cached = await this.getFromCache(url, userId);
            if (cached && !this.isExpired(cached)) {
              console.log(`✅ Image cache hit: ${strategy.description}`);
              return {
                success: true,
                imageData: cached.imageData,
                source: "cache"
              };
            }
            throw new Error("Cache miss or expired");

          case "direct_fetch":
            // Skip if we're already rate limited
            if (isRateLimited) {
              throw new Error("Skipping fetch - already rate limited");
            }
            const imageData = await this.fetchAndCacheImage(url, userId);
            console.log(`✅ Image direct fetch: ${strategy.description}`);
            return {
              success: true,
              imageData: imageData,
              source: "direct"
            };

          case "retry_with_delay":
            // Skip retry if we're rate limited - go straight to fallback
            if (isRateLimited) {
              console.log(`⏭️ Skipping retry strategy - rate limited, using fallback`);
              throw new Error("Skipping retry - rate limited");
            }
            await this.delay(strategy.maxDelay);
            const retryData = await this.fetchAndCacheImage(url, userId);
            console.log(`✅ Image retry success: ${strategy.description}`);
            return {
              success: true,
              imageData: retryData,
              source: "retry"
            };

          case "fallback_avatar":
            const fallbackData = this.generateFallbackAvatar(displayName, userId, url);
            console.log(`✅ Image fallback: ${strategy.description}`);
            return {
              success: true,
              imageData: fallbackData,
              source: "fallback"
            };

          default:
            throw new Error(`Unknown strategy: ${strategy.name}`);
        }
      } catch (error) {
        lastError = error;

        // Check if this is a rate limit error
        if (error.message && error.message.includes("Rate limited")) {
          isRateLimited = true;
          console.warn(`⚠️ Rate limited - skipping to fallback strategy`);
        } else {
          console.warn(`❌ Image strategy ${attempt + 1} failed: ${error.message}`);
        }

        // Add delay before next attempt (except for last strategy or if rate limited)
        if (attempt < this.retryStrategies.length - 1 && strategy.maxDelay > 0 && !isRateLimited) {
          await this.delay(Math.min(strategy.maxDelay, 1000));
        }
      }
    }

    // All strategies failed
    console.error(`❌ All image loading strategies failed. Last error:`, lastError?.message);

    // Return ultimate fallback
    return {
      success: false,
      imageData: this.generateFallbackAvatar(displayName, userId, url),
      source: "ultimate_fallback",
      error: lastError?.message
    };
  }

  /**
   * Get image from IndexedDB cache
   */
  async getFromCache(url, userId = null) {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], "readonly");
      const store = transaction.objectStore(this.storeName);
      const cacheKey = this.generateCacheKey(url, userId);

      const request = store.get(cacheKey);

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * Fetch image from URL and cache it
   */
  async fetchAndCacheImage(url, userId = null) {
    try {
      const response = await fetch(url);

      if (response.status === 429) {
        throw new Error("Rate limited by Google servers");
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const imageData = await this.blobToBase64(blob);

      // Cache the image
      await this.saveToCache(url, imageData, userId);

      return imageData;
    } catch (error) {
      console.error("Failed to fetch image:", error);
      throw error;
    }
  }

  /**
   * Save image to IndexedDB cache
   */
  async saveToCache(url, imageData, userId = null) {
    if (!this.db) await this.init();

    // Clean up old entries if needed
    await this.cleanupExpiredEntries();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);

      const cacheEntry = {
        id: this.generateCacheKey(url, userId),
        originalUrl: url,
        userId: userId,
        imageData: imageData,
        timestamp: Date.now(),
        size: imageData.length
      };

      const request = store.put(cacheEntry);

      request.onsuccess = () => {
        console.log(`💾 Image cached: ${url.substring(0, 50)}...`);
        resolve();
      };

      request.onerror = () => {
        console.error("Failed to cache image:", request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Convert blob to base64 string
   */
  async blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Check if cached entry is expired
   */
  isExpired(cacheEntry) {
    return (Date.now() - cacheEntry.timestamp) > this.cacheExpiration;
  }

  /**
   * Generate fallback avatar (initials or default)
   * @param {string} displayName - User's display name (e.g., "Isidro")
   * @param {string} userId - User's ID
   * @param {string} originalUrl - Original image URL (optional)
   */
  generateFallbackAvatar(displayName, userId, originalUrl) {
    let initials = "U";

    if (displayName) {
      // Extract initials from display name
      const nameParts = displayName.trim().split(" ");
      if (nameParts.length >= 2) {
        // First and last name: "John Doe" → "JD"
        initials = (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
      } else if (nameParts[0].length >= 2) {
        // Single name with 2+ chars: "Isidro" → "IS"
        initials = nameParts[0].substring(0, 2).toUpperCase();
      } else {
        // Single character name: "J" → "JU"
        initials = nameParts[0][0].toUpperCase() + "U";
      }
    } else if (userId) {
      // Fallback to userId
      initials = userId.substring(0, 2).toUpperCase();
    } else if (originalUrl && originalUrl.includes("googleusercontent.com")) {
      // Last resort for Google URLs
      initials = "G";
    }

    // Generate a deterministic color based on the initials
    const colors = [
      "#6366f1", // indigo
      "#8b5cf6", // purple
      "#ec4899", // pink
      "#ef4444", // red
      "#f97316", // orange
      "#eab308", // yellow
      "#22c55e", // green
      "#06b6d4", // cyan
      "#3b82f6", // blue
      "#a855f7", // violet
    ];
    const colorIndex = (initials.charCodeAt(0) + initials.charCodeAt(1)) % colors.length;
    const backgroundColor = colors[colorIndex];

    // Generate SVG avatar with initials
    const svg = `
      <svg width="96" height="96" xmlns="http://www.w3.org/2000/svg">
        <circle cx="48" cy="48" r="48" fill="${backgroundColor}"/>
        <text x="48" y="58" text-anchor="middle" font-family="Arial, sans-serif"
              font-size="28" font-weight="bold" fill="white">${initials}</text>
      </svg>
    `;

    return `data:image/svg+xml;base64,${btoa(svg)}`;
  }

  /**
   * Clean up expired cache entries
   */
  async cleanupExpiredEntries() {
    if (!this.db) return;

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);
      const index = store.index("timestamp");

      const request = index.openCursor();
      let deletedCount = 0;

      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          if (this.isExpired(cursor.value)) {
            cursor.delete();
            deletedCount++;
          }
          cursor.continue();
        } else {
          if (deletedCount > 0) {
            console.log(`🗑️ Cleaned up ${deletedCount} expired image cache entries`);
          }
          resolve();
        }
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * Clear all cached images
   */
  async clearCache() {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);

      const request = store.clear();

      request.onsuccess = () => {
        console.log("🗑️ Image cache cleared successfully");
        resolve();
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * Get cache statistics
   */
  async getCacheStats() {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], "readonly");
      const store = transaction.objectStore(this.storeName);

      const request = store.getAll();

      request.onsuccess = () => {
        const entries = request.result;
        const totalSize = entries.reduce((sum, entry) => sum + (entry.size || 0), 0);
        const expiredCount = entries.filter(entry => this.isExpired(entry)).length;

        resolve({
          totalEntries: entries.length,
          expiredEntries: expiredCount,
          totalSize: totalSize,
          oldestEntry: entries.length > 0 ? Math.min(...entries.map(e => e.timestamp)) : null,
          newestEntry: entries.length > 0 ? Math.max(...entries.map(e => e.timestamp)) : null
        });
      };

      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  /**
   * Utility function for delays
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Create singleton instance
const imageCacheService = new ImageCacheService();

export default imageCacheService;